import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Copy, Check, Play, Wand2, Sparkles, Lightbulb, Loader2, ChevronDown, Edit3, Save, RotateCcw, Eye, CheckCircle, AlertCircle } from 'lucide-react';
import { Prompt } from '../../types'; // Keep this import
import { useClickOutside } from '../../hooks/useClickOutside';
import { debounce } from '../../utils/helpers';
import { openAIService } from '../../services/openai';
import { useScrollLock } from '../../hooks/useScrollLock';

interface TemplateExecutorProps {
  template: Prompt;
  isOpen: boolean;
  onClose: () => void;
  onCopy?: (content: string) => void;
  onUpdateTemplate?: (id: string, updates: Partial<Prompt>, historyType?: 'enhanced' | 'manual' | 'variation') => Promise<void>;
}

export const TemplateExecutor: React.FC<TemplateExecutorProps> = ({
  template,
  isOpen,
  onClose,
  onCopy,
  onUpdateTemplate
}) => {
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);
  const [suggestions, setSuggestions] = useState<Record<string, string[]>>({});
  const [loadingSuggestions, setLoadingSuggestions] = useState<Record<string, boolean>>({});
  const [showSuggestions, setShowSuggestions] = useState<Record<string, boolean>>({});
  const [isLoadingCohesiveSet, setIsLoadingCohesiveSet] = useState(false);
  
  // Editable preview state
  const [isEditingPreview, setIsEditingPreview] = useState(false);
  const [editedPromptContent, setEditedPromptContent] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Save feedback states
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const modalRef = useClickOutside(() => {
    if (isOpen) {
      onClose();
    }
  });

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  // Memoize variables extraction to prevent unnecessary re-renders
  const variables = useMemo(() => {
    const matches = template.body.match(/\[([^\]]+)\]/g);
    if (!matches) return [];
    return [...new Set(matches.map(match => match.slice(1, -1)))];
  }, [template.body]);

  // Generate storage key for this template
  const storageKey = `template-${template.id}-variables`;

  // Debounced save to localStorage - only for persistence, not for state updates
  const debouncedSave = useCallback(
    debounce((values: Record<string, string>) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(values));
      } catch (error) {
        console.error('Error saving variable values:', error);
      }
    }, 500),
    [storageKey]
  );

  // Initialize variable values only when modal opens or template changes
  useEffect(() => {
    if (!isOpen) return;
    
    // Reset edited content when template changes
    setEditedPromptContent(null);
    setHasUnsavedChanges(false);
    setIsEditingPreview(false);
    
    const initializeValues = () => {
      try {
        const saved = localStorage.getItem(storageKey);
        let initialValues: Record<string, string> = {};
        
        if (saved) {
          const savedValues = JSON.parse(saved);
          // Ensure all current variables are included
          variables.forEach(variable => {
            initialValues[variable] = savedValues[variable] || '';
          });
        } else {
          // Initialize empty values
          variables.forEach(variable => {
            initialValues[variable] = '';
          });
        }
        
        setVariableValues(initialValues);
      } catch (error) {
        console.error('Error loading saved variable values:', error);
        // Initialize empty values on error
        const fallbackValues: Record<string, string> = {};
        variables.forEach(variable => {
          fallbackValues[variable] = '';
        });
        setVariableValues(fallbackValues);
      }
    };

    initializeValues();
    setCopied(false);
  }, [template.id, isOpen, storageKey]); // Removed variables from dependencies

  // Initialize any new variables that weren't in saved state
  useEffect(() => {
    if (Object.keys(variableValues).length > 0) {
      const hasAllVariables = variables.every(variable => variable in variableValues);
      if (!hasAllVariables) {
        setVariableValues(prev => {
          const updated = { ...prev };
          variables.forEach(variable => {
            if (!(variable in updated)) {
              updated[variable] = '';
            }
          });
          return updated;
        });
      }
    }
  }, [variables, variableValues]);

  // Save to localStorage whenever values change (debounced)
  useEffect(() => {
    if (Object.keys(variableValues).length > 0) {
      debouncedSave(variableValues);
    }
  }, [variableValues, debouncedSave]);

  // Simple, immediate variable change handler
  const handleVariableChange = (variable: string, value: string) => {
    setVariableValues(prev => ({
      ...prev,
      [variable]: value
    }));
  };

  // Handle AI suggestions
  const handleAISuggest = async (variable: string, isCreative: boolean = false) => {
    setLoadingSuggestions(prev => ({ ...prev, [variable]: true }));
    
    try {
      // Extract context from template and existing values
      const context = {
        aiPlatform: template.aiPlatform,
        domain: template.domain,
        output: template.output,
        platforms: template.platforms,
        webPlatforms: template.webPlatforms,
      };

      let suggestionResults: string[];

      if (isCreative) {
        // Use the generateVariableVariations method for creative suggestions
        suggestionResults = await openAIService.generateVariableVariations({
          originalPrompt: template.body,
          variableName: variable,
          isCreative: true,
          count: 5,
          context,
        });
      } else {
        // Use the suggestVariableInputs method for practical suggestions
        suggestionResults = await openAIService.suggestVariableInputs(
          template.body,
          variable,
          context
        );
      }

      setSuggestions(prev => ({
        ...prev,
        [variable]: suggestionResults,
      }));
      
      setShowSuggestions(prev => ({
        ...prev,
        [variable]: true,
      }));
    } catch (error) {
      console.error('Error getting AI suggestions:', error);
      
      // Show user-friendly error
      const errorMessage = error instanceof Error ? error.message : 'Failed to get suggestions';
      if (errorMessage.includes('API key not configured')) {
        console.warn('💡 AI suggestions unavailable - OpenAI API key not configured');
      } else {
        console.error('🔴 AI suggestions error:', errorMessage);
      }
    } finally {
      setLoadingSuggestions(prev => ({ ...prev, [variable]: false }));
    }
  };

  const handleSuggestionSelect = (variable: string, suggestion: string) => {
    handleVariableChange(variable, suggestion);
    setShowSuggestions(prev => ({ ...prev, [variable]: false }));
  };

  const closeSuggestions = (variable: string) => {
    setShowSuggestions(prev => ({ ...prev, [variable]: false }));
  };

  // Handle cohesive variable set generation
  const handleGenerateCohesiveSet = async () => {
    setIsLoadingCohesiveSet(true);
    
    try {
      // Extract context from template
      const context = {
        aiPlatform: template.aiPlatform,
        domain: template.domain,
        output: template.output,
        platforms: template.platforms,
        webPlatforms: template.webPlatforms,
      };

      // Generate cohesive suggestions for all variables
      const cohesiveSuggestions = await openAIService.generateCohesiveVariableSet(
        template.body,
        variables,
        context
      );

      // Update all variables at once with the cohesive set
      const newVariableValues: Record<string, string> = { ...variableValues };
      variables.forEach(variable => {
        if (cohesiveSuggestions[variable]) {
          newVariableValues[variable] = cohesiveSuggestions[variable];
        }
      });
      
      setVariableValues(newVariableValues);
      
      // Close any open individual suggestions
      setShowSuggestions({});
      
    } catch (error) {
      console.error('Error generating cohesive variable set:', error);
      // Could show an error toast here
    } finally {
      setIsLoadingCohesiveSet(false);
    }
  };

  // Process template with real-time variable replacement and highlighting
  const getProcessedPrompt = (): { html: string; text: string; editableText: string } => {
    // Use edited content if available, otherwise use original template
    const baseContent = editedPromptContent || template.body;
    let processedHtml = baseContent;
    let processedText = baseContent;
    let editableText = baseContent;
    
    variables.forEach(variable => {
      const value = variableValues[variable] || '';
      const placeholder = `[${variable}]`;
      const regex = new RegExp(`\\[${variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g');
      
      if (value.trim()) {
        // Replace with highlighted filled value
        processedHtml = processedHtml.replace(
          regex, 
          `<span class="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 px-2 py-1 rounded font-medium border border-blue-200 dark:border-blue-700">${value}</span>`
        );
        processedText = processedText.replace(regex, value);
      } else {
        // Keep placeholder but highlight it as unfilled
        processedHtml = processedHtml.replace(
          regex, 
          `<span class="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-2 py-1 rounded font-medium border border-yellow-200 dark:border-yellow-700">${placeholder}</span>`
        );
        processedText = processedText.replace(regex, placeholder);
      }
    });
    
    return { html: processedHtml, text: processedText, editableText };
  };

  // Handle preview editing
  const handleStartEditing = () => {
    const { editableText } = getProcessedPrompt();
    setEditedPromptContent(editableText);
    setIsEditingPreview(true);
    setSaveMessage(null); // Clear any previous save messages
  };

  const handleSaveChanges = async () => {
    if (!editedPromptContent || !onUpdateTemplate) {
      console.error('🔴 TemplateExecutor: Cannot save - missing content or callback');
      setSaveMessage({ type: 'error', text: 'Unable to save changes - missing data' });
      return;
    }

    console.log('🔵 TemplateExecutor: Starting save process');
    setIsSavingChanges(true);
    setSaveMessage(null);

    try {
      await onUpdateTemplate(template.id, { body: editedPromptContent.trim() }, 'manual');
      
      console.log('✅ TemplateExecutor: Save completed successfully');
      setHasUnsavedChanges(false);
      setIsEditingPreview(false);
      setSaveMessage({ type: 'success', text: 'Changes saved successfully!' });
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('🔴 TemplateExecutor: Save failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save changes';
      setSaveMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsSavingChanges(false);
    }
  };

  const handleResetToOriginal = () => {
    setEditedPromptContent(null);
    setHasUnsavedChanges(false);
    setIsEditingPreview(false);
  };

  const handleCancelEditing = () => {
    if (hasUnsavedChanges) {
      console.log('🔴 TemplateExecutor: Save blocked - missing data or callback', {
        hasEditedContent: !!editedPromptContent,
        hasCallback: !!onUpdateTemplate
      });
      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        setEditedPromptContent(null);
        setHasUnsavedChanges(false);
    console.log('🔵 TemplateExecutor: Starting save process', {
      templateId: template.id,
      originalBody: template.body.substring(0, 50) + '...',
      editedBody: editedPromptContent.substring(0, 50) + '...',
      hasCallback: !!onUpdateTemplate
    });

        setIsEditingPreview(false);
      console.log('🔵 TemplateExecutor: Calling onUpdateTemplate...');
      }
      console.log('✅ TemplateExecutor: Save completed successfully');
    } else {
      setIsEditingPreview(false);
    }
  };

  const handleEditedContentChange = (value: string) => {
    setEditedPromptContent(value);
    setHasUnsavedChanges(true);
    setSaveMessage(null); // Clear save messages when user starts editing
  };

  const handleCopy = async () => {
    const { text, editableText } = getProcessedPrompt();
    // Use the current editable text if in edit mode, otherwise use processed text
    const contentToCopy = isEditingPreview ? editableText : text;
    
    try {
      await navigator.clipboard.writeText(contentToCopy);
      setCopied(true);
      onCopy?.(contentToCopy);
      
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  const handleReset = () => {
    const resetValues: Record<string, string> = {};
    variables.forEach(variable => {
      resetValues[variable] = '';
    });
    setVariableValues(resetValues);
    setCopied(false);
    // Clear from localStorage
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('🔴 TemplateExecutor: Save failed:', error);
      alert(`Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const allVariablesFilled = variables.every(variable => variableValues[variable]?.trim());
  const { html: processedHtml, editableText } = getProcessedPrompt();

  // Render syntax-highlighted content for textarea overlay
  const renderSyntaxHighlighting = (content: string) => {
    return content.replace(/\[([^\]]+)\]/g, '<span style="background: rgba(59, 130, 246, 0.1); color: rgb(59, 130, 246); padding: 2px 4px; border-radius: 4px; font-weight: 600;">[$1]</span>');
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div ref={modalRef} className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
            <div className="flex items-center">
              <Play className="h-6 w-6 text-fire-600 dark:text-fire-400 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">
                  Template Executor
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {template.title}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Variable Input Bar */}
          {variables.length > 0 && ( // Keep this logic
            <div className="border-b border-gray-200 dark:border-dark-border bg-fire-50 dark:bg-fire-900/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text flex items-center">
                  <Wand2 className="h-5 w-5 mr-2 text-fire-500 dark:text-fire-400" />
                  Template Variables ({variables.length})
                </h3>
                <div className="flex items-center space-x-3">
                  {variables.length > 1 && (
                    <button
                      onClick={handleGenerateCohesiveSet}
                      disabled={isLoadingCohesiveSet}
                      className="flex items-center px-4 py-2 text-sm font-medium text-white 
                        bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 
                        rounded-lg transition-all duration-300 shadow-sm hover:shadow-md 
                        disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
                    >
                      {isLoadingCohesiveSet ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4 mr-2" />
                      )}
                      {isLoadingCohesiveSet ? 'Generating Set...' : '✨ Suggest All Variables'}
                    </button>
                  )}
                  
                  <button
                    onClick={handleReset}
                    className="text-sm text-fire-600 dark:text-fire-400 hover:text-fire-700 
                      dark:hover:text-fire-300 transition-colors font-medium"
                  >
                    Reset All
                  </button>
                </div>
              </div>
              
              {/* Cohesive Generation Info - REMOVED FOR CLEANER UI */}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {variables.map((variable, index) => (
                  <div key={variable} className="space-y-2 relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span className="inline-flex items-center">
                        <span className="font-mono text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded text-xs mr-2">
                          [{variable}] {/* Keep this text */}
                        </span>
                      </span>
                    </label>
                    
                    <input
                      type="text"
                      value={variableValues[variable] || ''}
                      onChange={(e) => handleVariableChange(variable, e.target.value)}
                      className="w-full px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800
                        rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 
                        focus:border-transparent text-gray-900 dark:text-dark-text 
                        placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300
                        hover:border-blue-300 dark:hover:border-blue-700"
                      placeholder={`Enter ${variable.replace(/[_-]/g, ' ').toLowerCase()}...`}
                      autoFocus={index === 0}
                    />

                    {/* AI Suggestion Buttons */}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => handleAISuggest(variable, false)}
                        disabled={loadingSuggestions[variable]} // Keep this logic
                        className="flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 
                          hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 
                          dark:hover:bg-blue-900/20 rounded-lg transition-all duration-300 
                          border border-blue-200 dark:border-blue-800 hover:border-blue-300 
                          dark:hover:border-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingSuggestions[variable] ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Lightbulb className="h-3 w-3 mr-1" />
                        )}
                        AI Suggest
                      </button>
                      
                      <button
                        onClick={() => handleAISuggest(variable, true)} // Keep this action
                        disabled={loadingSuggestions[variable]}
                        className="flex items-center px-3 py-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 
                          hover:text-purple-700 dark:hover:text-purple-300 hover:bg-purple-50 
                          dark:hover:bg-purple-900/20 rounded-lg transition-all duration-300 
                          border border-purple-200 dark:border-purple-800 hover:border-purple-300 
                          dark:hover:border-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loadingSuggestions[variable] ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Sparkles className="h-3 w-3 mr-1" />
                        )}
                        AI Creative
                      </button>
                    </div>

                    {/* AI Suggestions - Premium Horizontal Buttons */}
                    {showSuggestions[variable] && suggestions[variable] && ( // Keep this logic
                      <div className="mt-3 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 
                        rounded-xl border border-blue-200 dark:border-blue-800/30 relative">
                        
                        {/* Header with close button */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <Sparkles className="h-4 w-4 text-blue-500 dark:text-blue-400 mr-2" />
                            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                              AI Suggestions for [{variable}]
                            </span>
                          </div>
                          <button
                            onClick={() => closeSuggestions(variable)}
                            className="p-1.5 text-blue-400 dark:text-blue-500 hover:text-blue-600 dark:hover:text-blue-300
                              hover:bg-blue-100 dark:hover:bg-blue-800/30 rounded-lg transition-all duration-200"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Horizontal Suggestion Buttons */}
                        <div className="flex flex-wrap gap-2">
                          {suggestions[variable].map((suggestion, suggestionIndex) => (
                            <button
                              key={suggestionIndex} // Keep this key
                              onClick={() => handleSuggestionSelect(variable, suggestion)}
                              className="group relative px-4 py-2.5 bg-white dark:bg-dark-surface rounded-lg 
                                border border-blue-200 dark:border-blue-700/50 hover:border-blue-300 
                                dark:hover:border-blue-600 shadow-sm hover:shadow-md 
                                transition-all duration-200 transform hover:scale-[1.02] 
                                hover:bg-blue-50 dark:hover:bg-blue-900/30"
                            >
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 
                                group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                                {suggestion}
                              </span>
                              
                              {/* Subtle hover indicator */}
                              <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/5 to-purple-500/5 
                                opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                            </button>
                          ))}
                        </div>

                        {/* Subtle hint text */}
                        <p className="text-xs text-blue-600/70 dark:text-blue-400/70 mt-3 text-center">
                          Click any suggestion to use it in your template
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Template Display with Real-time Updates */}
          <div className="p-6 bg-gradient-to-br from-white to-gray-50 dark:from-dark-surface dark:to-dark-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text flex items-center"> {/* Keep this text */}
                <Play className="h-5 w-5 mr-2 text-fire-500 dark:text-fire-400" />
                {isEditingPreview ? 'Edit Preview' : 'Live Preview'}
              </h3>
              <div className="flex items-center space-x-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center">
                  <div className={`w-2 h-2 rounded-full mr-2 ${isEditingPreview ? 'bg-blue-500' : 'bg-green-500 animate-pulse'}`}></div>
                  {isEditingPreview ? 'Editing mode' : 'Updates in real-time'}
                </div>
                
                {!isEditingPreview ? (
                  <button
                    onClick={handleStartEditing}
                    className="flex items-center px-3 py-1.5 text-xs text-blue-600 dark:text-blue-400 
                      hover:text-blue-700 dark:hover:text-blue-300 hover:bg-blue-50 
                      dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 
                      border border-blue-200 dark:border-blue-800"
                  >
                    <Edit3 className="h-3 w-3 mr-1" />
                    Edit Preview
                  </button>
                ) : (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleCancelEditing}
                      className="flex items-center px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 
                        hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 
                        dark:hover:bg-gray-700 rounded-lg transition-all duration-200 
                        border border-gray-200 dark:border-gray-600"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </button>
                    <button
                      onClick={handleResetToOriginal}
                      className="flex items-center px-3 py-1.5 text-xs text-orange-600 dark:text-orange-400 
                        hover:text-orange-700 dark:hover:text-orange-300 hover:bg-orange-50 
                        dark:hover:bg-orange-900/20 rounded-lg transition-all duration-200 
                        border border-orange-200 dark:border-orange-800"
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Reset
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <div className="bg-white dark:bg-dark-surface rounded-lg border-2 border-gray-200 dark:border-dark-border shadow-sm overflow-hidden">
              {isEditingPreview ? (
                /* Editable Mode */
                <div className="relative">
                  <textarea
                    value={editedPromptContent || ''}
                    onChange={(e) => handleEditedContentChange(e.target.value)}
                    className="w-full p-6 text-gray-900 dark:text-dark-text leading-relaxed text-base 
                      bg-transparent border-none resize-none focus:outline-none min-h-[200px]
                      font-mono relative z-10 bg-white dark:bg-dark-surface"
                    placeholder="Edit your prompt here..."
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Monaco, Consolas, monospace' }}
                  />
                  
                  {/* Syntax highlighting overlay */}
                  <div 
                    className="absolute inset-0 p-6 text-base leading-relaxed whitespace-pre-wrap pointer-events-none
                      text-transparent z-0"
                    dangerouslySetInnerHTML={{ 
                      __html: renderSyntaxHighlighting(editedPromptContent || '') 
                    }}
                    style={{ fontFamily: 'ui-monospace, SFMono-Regular, Monaco, Consolas, monospace' }}
                  />
                </div>
              ) : (
                /* View Mode */
                <div className="p-6">
                  <div 
                    className="text-gray-900 dark:text-dark-text leading-relaxed whitespace-pre-wrap text-base"
                    dangerouslySetInnerHTML={{ __html: processedHtml }}
                  />
                </div>
              )}
            </div>
            
            {/* Edit Mode Controls */}
            {isEditingPreview && (
              <div className="space-y-4 mt-4">
                {/* Save feedback message */}
                {saveMessage && (
                  <div className={`flex items-center space-x-2 p-3 rounded-lg border ${
                    saveMessage.type === 'success'
                      ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
                      : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300'
                  }`}>
                    {saveMessage.type === 'success' ? (
                      <CheckCircle className="h-4 w-4 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium">{saveMessage.text}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 
                rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${
                      isSavingChanges ? 'bg-blue-500 animate-pulse' : 
                      hasUnsavedChanges ? 'bg-amber-500' : 'bg-emerald-500'
                    }`}></div>
                    <span className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                      {isSavingChanges ? 'Saving...' : hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved'}
                    </span>
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      Variables like [gemstone] are highlighted
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleSaveChanges}
                      disabled={!hasUnsavedChanges || isSavingChanges || !editedPromptContent?.trim()}
                      className="flex items-center px-4 py-2 text-sm font-medium text-white 
                        bg-blue-600 hover:bg-blue-700 rounded-lg transition-all duration-200 
                        shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed
                        transform hover:scale-105 min-w-[120px] justify-center"
                    >
                      {isSavingChanges ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
            <button
              onClick={handleCopy}
              className={`flex items-center px-4 py-2 text-sm rounded-lg transition-all duration-300 border font-medium ${ // Keep this logic
                copied
                  ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                  : 'text-fire-600 dark:text-fire-400 hover:text-fire-700 dark:hover:text-fire-300 hover:bg-fire-50 dark:hover:bg-fire-900/20 border-fire-200 dark:border-fire-800 hover:border-fire-300 dark:hover:border-fire-700'
              }`}
              title={isEditingPreview ? 'Copy edited version' : 'Copy filled template'}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  {isEditingPreview ? 'Copy Edited' : 'Copy Prompt'}
                </>
              )}
            </button>
            
            {/* Reset to Original button when edited */}
            {editedPromptContent && !isEditingPreview && (
              <button
                onClick={handleResetToOriginal}
                className="flex items-center px-4 py-2 text-sm font-medium text-orange-600 dark:text-orange-400 
                  hover:text-orange-700 dark:hover:text-orange-300 hover:bg-orange-50 
                  dark:hover:bg-orange-900/20 rounded-lg transition-all duration-200 
                  border border-orange-200 dark:border-orange-800"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset to Original
              </button>
            )}
            
            <button
              onClick={onClose} // Keep this action
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Floating Copy Button */}
      <button
        onClick={handleCopy}
        className={`fixed bottom-6 right-6 z-[60] w-16 h-16 rounded-full shadow-2xl
          transition-all duration-300 transform hover:scale-110 active:scale-95
          flex items-center justify-center group border-4 border-white dark:border-dark-surface
          ${copied
            ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/25'
            : 'bg-fire-gradient hover:shadow-fire-glow text-white'
          }`}
        title={copied ? 'Copied!' : (isEditingPreview ? 'Copy edited version' : 'Copy filled template')}
      >
        <div className={`absolute inset-0 rounded-full transition-opacity duration-300 ${
          copied 
            ? 'bg-emerald-600 opacity-0 group-hover:opacity-100' 
            : 'bg-modular-primary-gradient opacity-0 group-hover:opacity-100'
        }`} />
        
        {copied ? (
          <Check className="h-8 w-8 relative z-10 transition-transform duration-300" />
        ) : (
          <Copy className="h-8 w-8 relative z-10 group-hover:rotate-12 transition-transform duration-300" />
        )}
        
        {/* Subtle glow ring */}
        <div className={`absolute inset-0 rounded-full transition-all duration-300 ${
          copied // Keep this logic
            ? 'bg-emerald-500 opacity-20 animate-ping'
            : 'bg-fire-500 opacity-20 animate-ping group-hover:animate-none'
        }`} />
      </button>
    </>
  );
};